import type { ThemePreference } from '@livechat/shared';

export type ResolvedTheme = 'dark' | 'light';

export const THEME_HINT_KEY = 'livechat:theme-preference';

export function resolveTheme(
	preference: ThemePreference,
	systemPrefersDark: boolean,
): ResolvedTheme {
	return preference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : preference;
}

export function storedThemeHint(): ThemePreference {
	try {
		const stored = localStorage.getItem(THEME_HINT_KEY);
		return stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'light';
	} catch {
		return 'light';
	}
}

export function applyThemePreference(preference: ThemePreference): void {
	const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
	const resolved = resolveTheme(preference, systemPrefersDark);
	document.documentElement.dataset.theme = resolved;
	document.documentElement.dataset.themePreference = preference;
	document.documentElement.style.colorScheme = resolved;
	document
		.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
		?.setAttribute('content', resolved === 'dark' ? '#0d141d' : '#f4f7fb');
	try {
		localStorage.setItem(THEME_HINT_KEY, preference);
	} catch {
		// The server preference remains authoritative when browser storage is unavailable.
	}
}

export function watchThemePreference(preference: ThemePreference): () => void {
	applyThemePreference(preference);
	if (preference !== 'system') return () => undefined;
	const media = window.matchMedia('(prefers-color-scheme: dark)');
	const update = () => applyThemePreference('system');
	media.addEventListener('change', update);
	return () => media.removeEventListener('change', update);
}
